"use client"

import { useMemo } from "react"
import {
  localizeCharacterDisplay,
  type LocalizedCharacterDisplay,
} from "@/features/characters/lib/character-content-localization"
import { useCurrentLocale } from "@/shared/hooks/use-current-locale"
import type { Character } from "@/shared/types/character"

interface UseLocalizedCharacterOptions {
  fallbackCase?: "preserve" | "title"
}

export function useLocalizedCharacter(
  character: Character,
  options: UseLocalizedCharacterOptions = {}
): LocalizedCharacterDisplay {
  const locale = useCurrentLocale()
  const fallbackCase = options.fallbackCase ?? "preserve"

  return useMemo(
    () => localizeCharacterDisplay(character, locale, { fallbackCase }),
    [character, fallbackCase, locale]
  )
}

export function useLocalizedCharacters(
  characters: Character[],
  options: UseLocalizedCharacterOptions = {}
): LocalizedCharacterDisplay[] {
  const locale = useCurrentLocale()
  const fallbackCase = options.fallbackCase ?? "preserve"

  return useMemo(
    () =>
      characters.map((character) =>
        localizeCharacterDisplay(character, locale, { fallbackCase })
      ),
    [characters, fallbackCase, locale]
  )
}
